import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import ReactFlow, { Background, Controls, MarkerType } from "reactflow";
import "reactflow/dist/style.css";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Kpi, Panel, Badge2, Bar, PageHead } from "@/components/Bits";
import { useTheme } from "@/context/ThemeContext";

export default function CPM() {
  const { pid } = useParams();
  const { theme } = useTheme();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/projects/${pid}/cpm`).then(({ data }) => setData(data)); }, [pid]);

  const flow = useMemo(() => {
    if (!data) return { nodes: [], edges: [] };
    const dark = theme === "dark";
    const nodes = data.nodes.map((n, i) => ({
      id: n.id,
      position: { x: (n.es / Math.max(1, data.project_duration)) * 1400 + 40, y: 40 + (i % 6) * 130 },
      data: {
        label: (
          <div className="text-left leading-tight">
            <div className="text-[10px] font-mono opacity-70">{n.code}</div>
            <div className="text-[11px] font-semibold max-w-[150px] truncate">{n.name}</div>
            <div className="text-[10px] font-mono mt-1">{n.duration}d · TF {n.total_float}</div>
            <div className="text-[10px] font-mono opacity-70">{n.progress_pct}% done</div>
          </div>
        ),
      },
      style: {
        background: dark ? (n.critical ? "#2a0f10" : "#121212") : n.critical ? "#fef2f2" : "#ffffff",
        color: dark ? "#fafafa" : "#0a0a0a",
        border: `1px solid ${n.critical ? "#EF4444" : dark ? "#2A2A2A" : "#E5E7EB"}`,
        borderRadius: 4,
        padding: 8,
        width: 180,
        fontSize: 11,
      },
    }));
    const edges = data.nodes.flatMap((n) =>
      (n.predecessors || []).map((p) => {
        const pn = data.nodes.find((x) => x.id === p);
        const crit = pn?.critical && n.critical;
        return {
          id: `${p}-${n.id}`,
          source: p,
          target: n.id,
          animated: crit,
          style: { stroke: crit ? "#EF4444" : dark ? "#3f3f46" : "#94a3b8", strokeWidth: crit ? 2 : 1.2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: crit ? "#EF4444" : "#94a3b8" },
        };
      })
    );
    return { nodes, edges };
  }, [data, theme]);

  if (!data) return <div className="text-sm text-muted-foreground">Running forward & backward pass…</div>;

  return (
    <div data-testid="cpm-page">
      <PageHead title="Critical Path Engine (CPM)" subtitle="Forward/backward pass with Total Float & Free Float. Activities with TF = 0 drive the completion date.">
        <Badge2 tone="primary">{data.project_duration} day programme</Badge2>
        <Badge2 tone="bad">{data.critical_path.length} critical</Badge2>
      </PageHead>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Kpi label="Programme duration" value={`${data.project_duration} d`} tone="primary" testId="kpi-cpm-duration" />
        <Kpi label="Computed start" value={fmtDate(data.project_start)} />
        <Kpi label="Computed finish" value={fmtDate(data.project_finish)} />
        <Kpi label="Critical activities" value={data.critical_path.length} tone="bad" testId="kpi-cpm-critical" />
      </div>

      <Panel title="Activity Network Diagram — predecessors, successors & critical flow" testId="cpm-network-panel" className="mb-3">
        <div style={{ height: 460 }} className="border border-border">
          <ReactFlow nodes={flow.nodes} edges={flow.edges} fitView proOptions={{ hideAttribution: true }}>
            <Background gap={22} size={1} color={theme === "dark" ? "#1f1f22" : "#e2e8f0"} />
            <Controls />
          </ReactFlow>
        </div>
      </Panel>

      <Panel title="Schedule Table — ES / EF / LS / LF / Float" testId="cpm-table-panel">
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="w-full dense-table">
            <thead className="sticky-head">
              <tr>
                <th>Code</th><th>Activity</th><th className="text-right">Dur</th><th className="text-right">ES</th>
                <th className="text-right">EF</th><th className="text-right">LS</th><th className="text-right">LF</th>
                <th className="text-right">Total Float</th><th className="text-right">Free Float</th>
                <th>Early dates</th><th>Progress</th><th>Path</th>
              </tr>
            </thead>
            <tbody>
              {data.nodes.map((n) => (
                <tr key={n.id} className={n.critical ? "bg-red-500/5" : ""} data-testid={`cpm-row-${n.code}`}>
                  <td className="num text-primary">{n.code}</td>
                  <td className="max-w-[240px] truncate" title={n.name}>{n.name}</td>
                  <td className="num text-right">{n.duration}</td>
                  <td className="num text-right">{n.es}</td>
                  <td className="num text-right">{n.ef}</td>
                  <td className="num text-right">{n.ls}</td>
                  <td className="num text-right">{n.lf}</td>
                  <td className={`num text-right font-semibold ${n.total_float <= 0 ? "text-red-500" : n.total_float < 5 ? "text-amber-500" : "text-emerald-500"}`}>{n.total_float}</td>
                  <td className="num text-right">{n.free_float}</td>
                  <td className="num text-[11px] text-muted-foreground">{fmtDate(n.es_date)} → {fmtDate(n.ef_date)}</td>
                  <td><Bar pct={n.progress_pct} /></td>
                  <td>{n.critical ? <Badge2 tone="bad">Critical</Badge2> : <Badge2>Float</Badge2>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
